import {
  CloudUploadOutlined,
  DownloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import { AppButton } from '@components/AppComponents/AppButton/index'
import { useStoreContext } from '@components/StoreContextWrapper'
import { ApiService } from '@services/ApiService'
import appStore from '@stores/appStore'
import {
  DEFAULT_FILE_TYPE,
  PURVIEW_PROTECTED_FILE_TYPE,
} from '@utils/constants'
import {
  getFileExtension,
  getServerErrorMessage,
  needsManualErrorNotification,
} from '@utils/functions'
import {
  PurviewUploadMode,
  isPurviewUploadChooserEnabled,
} from '@utils/purviewProtection'
import { Spin, Tooltip, Upload, notification } from 'antd'
import { observer } from 'mobx-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import './styles.scss'

import { useTrackedAction } from '../../../activity/ActivitySubjectContext'

const { Dragger } = Upload

export const AppFileUpload = observer(
  ({
    title = 'Click or drag file to upload',
    name = 'file',
    type = DEFAULT_FILE_TYPE,
    description = '',
    multiUpload = false,
    itemRender = null,
    maxSize = 100,
    previewTemplate = null,
    fileList = [],
    attachmentTypeId = null,
    readonly = false,
    allType = null,
    onChange = (listFiles, file, formData?) => {},
    beforeUpload = file => {},
    onUploadsSettled = () => {},
    tenantId = null,
    handleClose = () => {},
  }) => {
    const { setContextLoading } = useStoreContext()
    const [_fileList, setFileList] = useState(fileList)
    const [imgPreview, setImgPreview] = useState<any>('')
    const [loading, setLoading] = useState<boolean>(false)
    // Extra line shown under the spinner when the server is doing something slow that the user
    // would otherwise read as the app having hung — removing a Purview label takes a few seconds.
    const [progressHint, setProgressHint] = useState<string>('')
    useEffect(() => {
      if (fileList.length) {
        setFileList(fileList.map(file => ({ ...file, status: 'done' })))
      }
    }, [fileList])

    // One switch for the whole chooser — code kill switch, then signed-in user, then the tenant's
    // Purview configuration, plus wherever role gating gets added. See isPurviewUploadChooserEnabled.
    const chooserEnabled = useMemo(
      () => isPurviewUploadChooserEnabled(),
      [appStore.currentTenant, appStore.currentUser]
    )

    // With the chooser on, anything that is not an explicit button press — dragging a file in —
    // counts as a plain upload, so a labelled file is refused rather than silently decrypted.
    // With it off there are no buttons to redirect anyone to, so refusing would leave the user
    // stuck: fall back to Auto, exactly what happened before the chooser existed.
    const defaultUploadMode = chooserEnabled
      ? PurviewUploadMode.Standard
      : PurviewUploadMode.Auto

    // Count pending uploads instead of tracking a boolean: with multiUpload,
    // every picked file is counted in up front and released only when its own
    // upload settles, so a boolean would flip loading off as soon as the FIRST
    // file finished — re-enabling consumers (e.g. Save buttons gated on
    // contextLoading) while the remaining files are still queued.
    const inFlightUploads = useRef(0)

    // Uploads run one at a time. antd calls beforeUpload for every picked file
    // in the same tick, and firing them all at once (20+ files) trips the API
    // throttle with 429s. Each upload chains onto the previous one, and the
    // chain swallows rejections so one failed file never stalls the rest.
    const uploadQueue = useRef<Promise<void>>(Promise.resolve())

    // Activity history: the files picked in one go are one tracked action (Common.File.Upload),
    // open from the first upload until every queued upload has settled, so the upload requests
    // and whatever onChange triggers (some fields save the attachment straight away) join it.
    const trackAction = useTrackedAction()
    const settleUploadActivity = useRef<(() => void) | null>(null)
    const startUploadActivity = (purviewMode: PurviewUploadMode) => {
      if (settleUploadActivity.current) {
        return
      }
      let settle = () => {}
      const settled = new Promise<void>(resolve => {
        settle = resolve
      })
      settleUploadActivity.current = () => {
        settleUploadActivity.current = null
        settle()
      }
      trackAction(
        purviewMode === PurviewUploadMode.Labeled
          ? 'Common.File.UploadLabelled'
          : 'Common.File.Upload',
        () => settled
      ).catch(() => {})
    }

    // Unmounted mid-upload: let the upload action end instead of waiting for its max lifetime.
    useEffect(() => () => settleUploadActivity.current?.(), [])

    const enqueueUpload = (task: () => Promise<void>) => {
      const next = uploadQueue.current.then(task)
      uploadQueue.current = next.catch(() => {})
      return next
    }
    const onLoading = status => {
      inFlightUploads.current = Math.max(
        0,
        inFlightUploads.current + (status ? 1 : -1)
      )
      const anyInFlight = inFlightUploads.current > 0
      setContextLoading(anyInFlight)
      setLoading(anyInFlight)
      if (!status && !anyInFlight) {
        try {
          onUploadsSettled()
        } finally {
          settleUploadActivity.current?.()
        }
      }
    }

    const _beforeUpload = async (
      file,
      fileList,
      purviewMode?: PurviewUploadMode
    ) => {
      const uploadMode = purviewMode ?? defaultUploadMode
      let fileType = getFileExtension(file.name)

      beforeUpload(file)
      const match = allType ? true : type.indexOf(fileType) !== -1

      if (match) {
        setImgPreview('')
        startUploadActivity(uploadMode)
        onLoading(true)
        const size = file.size / 1024 / 1000
        if (size > maxSize) {
          notification.error({
            message: 'Error',
            description: `The file you have selected is too large. The maximum size is ${maxSize}MB.`,
          })
          onLoading(false)
          return Upload.LIST_IGNORE
        }

        let fL = [..._fileList]
        if (multiUpload) {
          if (fL.length >= maxSize) {
            notification.warning({
              message: 'Warning',
              description: `You can only upload ${maxSize} files`,
            })
            onLoading(false)
            return Upload.LIST_IGNORE
          } else {
            fL = [..._fileList, file]
          }
        } else {
          fL = [file]
          if (type.indexOf(fileType) !== -1) {
            let img
            try {
              img = await toBase64(file)
            } catch {
              // The file could not be read (removed or locked since it was picked): release its
              // count, so the loading state and the upload's activity scope do not stay open.
              notification.error({
                message: 'Error',
                description: `${file.name} could not be read.`,
              })
              onLoading(false)
              return Upload.LIST_IGNORE
            }
            setImgPreview(img)
          }
        }
        await enqueueUpload(() => handleUpload(file, fL, uploadMode))
      } else {
        notification.error({
          message: 'Error',
          description: `Only '${type.join(', ')}' file accepted`,
        })
      }
      return Upload.LIST_IGNORE
    }
    let totalCount = 0

    const apiUpload = (formData, purviewMode: PurviewUploadMode) => {
      if (!appStore.currentUser?.id) {
        // The anonymous LEAP endpoint has no buttons and no user to ask, so it keeps the
        // server-side default (Auto).
        return ApiService.File.UploadAnonymousFile(formData, tenantId)
      }
      return ApiService.File.Upload(formData, purviewMode)
    }
    const handleUpload = async (
      file,
      fL,
      purviewMode: PurviewUploadMode = PurviewUploadMode.Auto
    ) => {
      const formData = new FormData()

      formData.append('name', file.name)
      formData.append('file', file)
      formData.append('type', getFileExtension(file.name))

      // NOTE: no onLoading(true) here — _beforeUpload (the only caller) already
      // counted this upload in; a second increment would never be paired with a
      // decrement (the finally below releases exactly one).
      const fl = [...fL]

      // Removing a Microsoft Purview label happens server-side and takes a few seconds, so say so
      // rather than leaving a bare spinner.
      if (
        purviewMode === PurviewUploadMode.Labeled ||
        PURVIEW_PROTECTED_FILE_TYPE.includes(getFileExtension(file.name))
      ) {
        setProgressHint(
          'Removing the sensitivity label — this can take a few seconds…'
        )
      }

      return apiUpload(formData, purviewMode)
        .then(({ data }) => {
          if (data.id) {
            fl[fl.length - 1] = {
              ...fl[fl.length - 1],
              ...data,
              id: null,
              attachmentTypeId,
              status: 'done',
              fileId: data.id,
              notSave: true,
              fileSizeInByte: file.size,
            }
            onChange([...fl], fl[fl.length - 1], formData)
            totalCount++
          } else {
            // No id but a 2xx: nothing useful to relay, so the generic message stands.
            notification.error({
              message: 'Error',
              description: `${file.name} file upload failed.`,
            })
            fl[fl.length - 1].status = 'error'
          }
        })
        .catch(err => {
          // The axios interceptor in baseService already shows the server's own message — but only
          // for a non-500 response. Adding the generic "file upload failed" on top of that buried
          // the part the user can act on (a storage quota, or a Microsoft Purview label this system
          // is not authorised to remove), while staying quiet on a 500 hid the failure altogether.
          if (needsManualErrorNotification(err)) {
            notification.error({
              message: 'Error',
              description:
                getServerErrorMessage(err) ||
                `${file.name} file upload failed.`,
            })
          }
          fl[fl.length - 1].status = 'error'
        })
        .finally(() => {
          setProgressHint('')
          onLoading(false)
          setFileList([...fl])
        })
    }

    const toBase64 = file =>
      new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })

    const removeFile = file => {
      appStore.openConfirmModal({
        message: 'Do you want to delete this file?',
        onOk: () => {
          const { uid } = file
          const fL = [..._fileList]
          const index = fL.findIndex(f => f.uid === uid)
          fL.splice(index, 1)
          setFileList([...fL])
          onChange([...fL], { ...file, status: 'removed' })
          if (!multiUpload) {
            setImgPreview('')
          }
          if (fL.length === 0) {
            handleClose ?? handleClose()
          }
        },
      })
    }

    const acceptTypes = useMemo(() => {
      return type.map(i => `.${i}`).join(',')
    }, [type])

    const overlayHidden = readonly || loading || appStore.isDemoAccount

    /**
     * The two explicit choices, revealed over the drop area on hover.
     *
     * Rendered outside the Dragger so a click lands on the button and not on the Dragger's own
     * picker — the whole point is that the two paths are distinguishable. The container stays
     * pointer-events-none so dragging a file onto the area still reaches the Dragger everywhere
     * except the buttons themselves; only the buttons take pointer events back.
     *
     * Dropping a file (rather than clicking) counts as a plain upload. That is the safe default:
     * a labelled file dropped here is refused with a message naming the other button, never
     * silently decrypted.
     */
    const uploadOverlay = (
      <div className="absolute inset-0 z-10 flex items-center justify-center gap-3 transition-all duration-200 opacity-0 pointer-events-none invisible group-hover:opacity-100 group-hover:visible bg-white/85">
        <div className="pointer-events-auto">
          <Upload
            accept={acceptTypes}
            showUploadList={false}
            multiple={multiUpload}
            beforeUpload={(file, list) => {
              _beforeUpload(file, list, PurviewUploadMode.Standard)
              return Upload.LIST_IGNORE
            }}
          >
            <Tooltip title="Upload a normal file. It is stored exactly as it is, with no processing. If it turns out to carry a Microsoft Purview sensitivity label, the upload is refused rather than stored unreadable.">
              <AppButton color="blue">
                <CloudUploadOutlined /> Upload file
              </AppButton>
            </Tooltip>
          </Upload>
        </div>

        <div className="pointer-events-auto">
          <Upload
            accept={acceptTypes}
            showUploadList={false}
            multiple={multiUpload}
            beforeUpload={(file, list) => {
              _beforeUpload(file, list, PurviewUploadMode.Labeled)
              return Upload.LIST_IGNORE
            }}
          >
            <Tooltip title="Use this for a document carrying a Microsoft Purview sensitivity label. Kawaconn removes the protection during upload and stores the readable original, so page count, search, redaction and preview keep working. Takes a few seconds longer.">
              <AppButton color="green">
                <SafetyCertificateOutlined /> Upload labelled file
              </AppButton>
            </Tooltip>
          </Upload>
        </div>
      </div>
    )

    const dragger = (
      <Dragger
        className="fileupload upload-list-inline"
        accept={acceptTypes}
        beforeUpload={_beforeUpload}
        name={name}
        onDownload={file => ApiService.File.Download(file)}
        itemRender={itemRender}
        fileList={fileList}
        disabled={readonly || loading || appStore.isDemoAccount}
        showUploadList={{
          showDownloadIcon: true,
          showRemoveIcon: !readonly,
          downloadIcon: (
            <DownloadOutlined className="text-blue-400 inline" width="14" />
          ),
        }}
        multiple={multiUpload}
        onRemove={file => {
          trackAction('Common.File.Remove', () => removeFile(file)).catch(
            () => {}
          )
        }}
      >
        {!readonly && (
          <div className="flex px-6">
            <div className="flex justify-center mr-6">
              {loading ? (
                <Spin />
              ) : (
                <div>
                  {(multiUpload ||
                    !imgPreview ||
                    (!multiUpload && !!_fileList.length)) && (
                    <CloudUploadOutlined
                      style={{ fontSize: 50, color: 'var(--blue-400)' }}
                    />
                  )}
                  {!multiUpload &&
                    imgPreview &&
                    !!previewTemplate &&
                    (previewTemplate || (
                      <div className="fileupload__preview">
                        <img src={imgPreview} alt="preview" />
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="flex justify-center flex-col">
              <p className="text-left ant-upload-text text-blue-400">
                {imgPreview ||
                !!previewTemplate ||
                (!multiUpload && !!_fileList.length)
                  ? 'Click or drag file to replace'
                  : title}
              </p>
              <p className="text-left ant-upload-hint">
                {loading && progressHint ? progressHint : description}
              </p>
            </div>
          </div>
        )}
      </Dragger>
    )

    // Chooser off — render the bare Dragger, with no wrapper at all, so the markup is identical to
    // what it was before this feature existed. Anything that depends on this component's DOM or
    // styling stays untouched for every tenant that has not turned Purview on.
    if (!chooserEnabled) {
      return dragger
    }

    return (
      <div className="relative group">
        {!overlayHidden && uploadOverlay}
        {dragger}
      </div>
    )
  }
)
